/* eslint-disable max-lines */
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { debounce } from "lodash";
import { ChangeEvent, FC, useMemo, useState } from "react";
import { DateRange } from "react-day-picker";
import { useSearchParams } from "react-router-dom";
import { cx } from "../../utils/classnames";
import {
  SOURCE_OPTIONS,
  STATUS_OPTIONS
} from "../../utils/customerFeedback/labels";
import RangePicker from "../ui/datepicker/range/RangePicker";
import Input from "../ui/input/Input";
import { SelectOption } from "../ui/input/Input.types";
import SelectInput from "../ui/input/SelectInput";

// Filter bar for the Customer Feedback list. State lives in the URL
// so a filtered view can be bookmarked / shared. The list page reads
// the same searchParams to drive its API query — no callback wiring
// needed between the two.
const FeedbackFilters: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const source = searchParams.get("source") ?? "";
  const status = searchParams.get("status") ?? "";
  const hasEmail = searchParams.get("hasEmail") ?? "";
  const route = searchParams.get("route") ?? "";
  const dateFrom = searchParams.get("dateFrom") ?? "";
  const dateTo = searchParams.get("dateTo") ?? "";

  // SelectInput is a single-open dropdown; coordinating which one
  // is open via a shared key keeps stacking order tidy when the user
  // clicks between filters.
  const [openDropdown, setOpenDropdown] = useState<
    "source" | "status" | "hasEmail" | null
  >(null);

  // Tiny helper to update one key without clobbering the rest.
  // Removes the key entirely on empty value so the URL stays clean.
  const update = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === "") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    // Reset to page 1 whenever a filter changes — otherwise we'd
    // land on an empty page if the new filter has fewer pages.
    next.delete("pageindex");
    setSearchParams(next, { replace: false });
  };

  // Debounced route search: typing fires the GET 700ms after the
  // last keystroke (mirrors the global header search behavior).
  const debouncedRoute = useMemo(
    () =>
      debounce((value: string) => {
        update("route", value.trim());
      }, 700),
    [searchParams]
  );

  const onSearchInput = (event: ChangeEvent<HTMLInputElement>) =>
    debouncedRoute(event.currentTarget.value);

  // Build inclusive day boundaries in local time. We send full
  // ISO timestamps (00:00:00.000 start, 23:59:59.999 end) so the
  // backend's `>= dateFrom AND <= dateTo` comparison covers the
  // entire selected day regardless of how the records' insertDate
  // times fall — picking just "today" needs to include 13:42 etc.,
  // not stop at midnight.
  const startOfDayISO = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}T00:00:00.000`;
  };
  const endOfDayISO = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}T23:59:59.999`;
  };

  const dateRange = useMemo<DateRange | undefined>(() => {
    if (!dateFrom && !dateTo) return undefined;
    return {
      from: dateFrom ? new Date(dateFrom) : undefined,
      to: dateTo ? new Date(dateTo) : undefined
    };
  }, [dateFrom, dateTo]);

  const handleDateChange = (r: DateRange | undefined) => {
    const next = new URLSearchParams(searchParams);
    next.delete("pageindex");
    if (r?.from) next.set("dateFrom", startOfDayISO(r.from));
    else next.delete("dateFrom");
    if (r?.to) next.set("dateTo", endOfDayISO(r.to));
    else next.delete("dateTo");
    setSearchParams(next, { replace: false });
  };

  const clearAll = () => {
    setSearchParams(new URLSearchParams(), { replace: false });
  };

  const hasAnyFilter =
    source !== "" ||
    status !== "" ||
    hasEmail !== "" ||
    dateFrom !== "" ||
    dateTo !== "" ||
    route !== "";

  // SelectInput matches `opt.id === selectedId` strictly, so we
  // stringify the enum values to align with the URL string params.
  const sourceOptions = useMemo<SelectOption[]>(
    () => [
      { id: "", name: "Të gjitha burimet" },
      ...SOURCE_OPTIONS.map((o) => ({ id: String(o.value), name: o.label }))
    ],
    []
  );
  const statusOptions = useMemo<SelectOption[]>(
    () => [
      { id: "", name: "Të gjitha statuset" },
      ...STATUS_OPTIONS.map((o) => ({ id: String(o.value), name: o.label }))
    ],
    []
  );
  const hasEmailOptions = useMemo<SelectOption[]>(
    () => [
      { id: "", name: "Të gjitha" },
      { id: "true", name: "Me email" },
      { id: "false", name: "Anonim" }
    ],
    []
  );

  const toggleDropdown = (key: "source" | "status" | "hasEmail") =>
    setOpenDropdown((prev) => (prev === key ? null : key));

  return (
    <div className="flex flex-wrap items-end gap-3 mb-4">
      <SelectInput
        className="w-44"
        label="Burimi"
        placeholder="Të gjitha burimet"
        options={sourceOptions}
        selectedId={source}
        isOpen={openDropdown === "source"}
        onClick={() => toggleDropdown("source")}
        onOptionSelect={(opt) => update("source", String(opt?.id ?? ""))}
      />

      <SelectInput
        className="w-44"
        label="Statusi"
        placeholder="Të gjitha statuset"
        options={statusOptions}
        selectedId={status}
        isOpen={openDropdown === "status"}
        onClick={() => toggleDropdown("status")}
        onOptionSelect={(opt) => update("status", String(opt?.id ?? ""))}
      />

      <SelectInput
        className="w-36"
        label="Email"
        placeholder="Të gjitha"
        options={hasEmailOptions}
        selectedId={hasEmail}
        isOpen={openDropdown === "hasEmail"}
        onClick={() => toggleDropdown("hasEmail")}
        onOptionSelect={(opt) => update("hasEmail", String(opt?.id ?? ""))}
      />

      <div className="w-[260px]">
        <RangePicker
          label="Periudha"
          placeholder="Zgjedh datat"
          range={dateRange}
          showApply={false}
          paddingType="hug"
          iconSide="right"
          iconColor="secondary"
          onChange={handleDateChange}
        />
      </div>

      {hasAnyFilter && (
        <button
          type="button"
          onClick={clearAll}
          className="text-sm text-gray-600 hover:text-primary underline self-end pb-3 transition-colors"
        >
          Pastro
        </button>
      )}

      <Input
        className="group ml-auto"
        defaultValue={route}
        placeholder="Kërko linjën..."
        onChange={onSearchInput}
        inputClassName={cx([
          "group h-11",
          route.length
            ? "w-56 pr-1"
            : "cursor-pointer sm:focus:w-56 focus:w-48 w-11 pr-1"
        ])}
        beforeClassName="text-neutral group-hover:text-black"
        before={
          <MagnifyingGlassIcon className="w-4 h-4" strokeWidth={2} />
        }
      />
    </div>
  );
};

export default FeedbackFilters;
